/**
 * Shop The Look Drawer Component
 * Opens a drawer with CTL products, separate option selectors, and add-to-cart functionality
 */

if (!customElements.get('shop-the-look-drawer')) {
  class ShopTheLookDrawer extends HTMLElement {
    constructor() {
      super();

      // Element references
      this.overlay = this.querySelector('[data-stl-overlay]');
      this.closeButton = this.querySelector('[data-stl-close]');
      this.productCards = this.querySelectorAll('[data-stl-product-card]');
      this.totalDisplay = this.querySelector('[data-stl-total]');
      this.addAllButton = this.querySelector('[data-stl-add-all]');
      this.addAllButtonText = this.addAllButton?.querySelector('.stl-drawer__add-all-text');
      this.addAllSpinner = this.addAllButton?.querySelector('.loading__spinner');
      this.errorContainer = this.querySelector('[data-stl-error]');
      this.errorText = this.querySelector('[data-stl-error-text]');

      // State
      this.activeElement = null;
      this.selections = new Map(); // productId -> variantId
      this.isAdding = false;

      // Parse product data
      const jsonScript = this.querySelector('[data-stl-products-json]');
      this.productsData = jsonScript ? JSON.parse(jsonScript.textContent) : [];

      // Bind
      this.handleKeyUp = this.handleKeyUp.bind(this);

      this.bindEvents();
      this.updateFooter();
    }

    bindEvents() {
      this.addEventListener('keyup', this.handleKeyUp);
      this.overlay?.addEventListener('click', () => this.close());
      this.closeButton?.addEventListener('click', () => this.close());

      // Option select changes (separate dropdowns per option)
      this.querySelectorAll('[data-stl-option-select]').forEach(select => {
        select.addEventListener('change', (e) => this.handleOptionChange(e));
      });

      // Include checkboxes
      this.querySelectorAll('[data-stl-include-check]').forEach(cb => {
        cb.addEventListener('change', (e) => this.handleIncludeCheck(e));
      });

      // Add all button
      this.addAllButton?.addEventListener('click', () => this.handleAddAll());

      // Trigger delegation — only this drawer responds when the trigger is
      // the same one already used to open it; OR if no other drawer has
      // claimed it. This prevents multiple drawer instances all opening at
      // once when there are duplicate <shop-the-look-drawer> elements on
      // the page (e.g. sticky/mobile-mirror sections).
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-stl-open]');
        if (!trigger) return;
        if (trigger.dataset.stlClaimed && trigger.dataset.stlClaimed !== this.id) return;
        if (!trigger.dataset.stlClaimed) trigger.dataset.stlClaimed = this.id;
        e.preventDefault();
        this.open(trigger);
      });
    }

    // ----- OPEN / CLOSE -----

    open(triggeredBy = null) {
      if (triggeredBy) this.activeElement = triggeredBy;

      if (!this._originalParent) {
        this._originalParent = this.parentElement;
        this._originalNextSibling = this.nextElementSibling;
      }

      document.body.appendChild(this);
      this.hideError();

      setTimeout(() => {
        this.classList.add('active');
      });

      this.addEventListener('transitionend', () => {
        if (!this.classList.contains('active')) return;
        const focusTarget = this.querySelector('.stl-drawer__inner') || this.closeButton;
        if (typeof trapFocus === 'function') {
          trapFocus(this, focusTarget);
        }
      }, { once: true });

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');

      if (typeof removeTrapFocus === 'function') {
        try { removeTrapFocus(this.activeElement); } catch (e) { /* ignore */ }
      }
    }

    handleKeyUp(event) {
      if (event.code === 'Escape') this.close();
    }

    // ----- INCLUDE CHECKBOX -----

    handleIncludeCheck(event) {
      const cb = event.target;
      const card = cb.closest('[data-stl-product-card]');
      const productId = card.dataset.productId;

      if (cb.checked) {
        // For single-variant products, variantId is on the checkbox itself
        const directVariantId = cb.dataset.variantId;
        if (directVariantId) {
          this.selections.set(productId, parseInt(directVariantId));
        } else {
          // Multi-variant — resolve from current select values
          const product = this.productsData.find(p => String(p.id) === productId);
          if (product) {
            const optionSelects = card.querySelectorAll('[data-stl-option-select]');
            const selectedOptions = {};
            optionSelects.forEach(sel => {
              if (sel.value) selectedOptions[parseInt(sel.dataset.optionPosition)] = sel.value;
            });
            const variant = product.variants.find(v => {
              if (!v.available) return false;
              return Object.entries(selectedOptions).every(([pos, val]) => v.options[pos - 1] === val);
            });
            if (variant) this.selections.set(productId, variant.id);
          }
        }
      } else {
        this.selections.delete(productId);
      }

      card.querySelector('[data-stl-select-prompt]')?.classList.add('hidden');
      this.updateFooter();
    }

    // ----- OPTION SELECTION -----

    handleOptionChange(event) {
      const select = event.target;
      const card = select.closest('[data-stl-product-card]');
      const productId = card.dataset.productId;
      const priceDisplay = card.querySelector('[data-stl-price]');
      const prompt = card.querySelector('[data-stl-select-prompt]');

      // Collect all selected options from this card's dropdowns
      const optionSelects = card.querySelectorAll('[data-stl-option-select]');
      const selectedOptions = {};
      let allOptionsSelected = true;

      optionSelects.forEach(sel => {
        const position = parseInt(sel.dataset.optionPosition);
        if (sel.value) {
          selectedOptions[position] = sel.value;
        } else {
          allOptionsSelected = false;
        }
      });

      const checkbox = card.querySelector('[data-stl-include-check]');

      if (allOptionsSelected && Object.keys(selectedOptions).length > 0) {
        // All visible options selected — find matching available variant
        const product = this.productsData.find(p => String(p.id) === productId);
        if (product) {
          const matchingVariant = product.variants.find(v => {
            if (!v.available) return false;
            return Object.entries(selectedOptions).every(([pos, val]) => {
              return v.options[pos - 1] === val;
            });
          });

          if (matchingVariant) {
            // Enable checkbox now that a valid variant is resolved
            if (checkbox) checkbox.disabled = false;
            // If already checked, keep selection in sync
            if (checkbox && checkbox.checked) {
              this.selections.set(productId, matchingVariant.id);
            }
            if (priceDisplay) priceDisplay.textContent = this.stripHtml(matchingVariant.formattedPrice);
          } else {
            // Combination is sold out — disable + uncheck
            if (checkbox) { checkbox.disabled = true; checkbox.checked = false; }
            this.selections.delete(productId);
            if (priceDisplay) priceDisplay.textContent = 'Sold Out';
          }
        }
      } else {
        // Not all options selected yet — disable + uncheck
        if (checkbox) { checkbox.disabled = true; checkbox.checked = false; }
        this.selections.delete(productId);
      }

      // Hide validation prompt on interaction
      prompt?.classList.add('hidden');

      this.updateFooter();
    }

    // ----- FOOTER STATE -----

    updateFooter() {
      const count = this.selections.size;

      if (count === 0) {
        if (this.addAllButtonText) this.addAllButtonText.textContent = 'SELECT SIZES';
        if (this.addAllButton) this.addAllButton.disabled = true;
        if (this.totalDisplay) this.totalDisplay.textContent = '--';
      } else {
        if (this.addAllButtonText) this.addAllButtonText.textContent = `ADD ${count} TO BAG`;
        if (this.addAllButton) this.addAllButton.disabled = false;

        // Sum selected variant prices
        let sum = 0;
        this.selections.forEach((variantId, productId) => {
          const product = this.productsData.find(p => String(p.id) === productId);
          const variant = product?.variants.find(v => v.id === variantId);
          if (variant) sum += variant.price;
        });
        if (this.totalDisplay) this.totalDisplay.textContent = this.formatMoney(sum);
      }
    }

    // ----- ADD TO CART -----

    async handleAddAll() {
      if (this.isAdding || this.selections.size === 0) return;

      this.isAdding = true;
      this.addAllButton.classList.add('loading');
      this.addAllButton.disabled = true;
      this.addAllSpinner?.classList.remove('hidden');
      this.hideError();

      try {
        const entries = Array.from(this.selections.entries());
        let lastResponse = null;

        for (let i = 0; i < entries.length; i++) {
          const [, variantId] = entries[i];
          const isLast = i === entries.length - 1;
          lastResponse = await this.addToCart(variantId, isLast);
        }

        // Publish cart update
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'shop-the-look',
            cartData: lastResponse
          });
        }

        // Open cart drawer
        this.openCartDrawer(lastResponse);

        // Close STL drawer
        this.close();
      } catch (error) {
        this.showError(error.message || 'Could not add items to cart. Please try again.');
      } finally {
        this.isAdding = false;
        this.addAllButton.classList.remove('loading');
        this.addAllSpinner?.classList.add('hidden');
        this.updateFooter();
      }
    }

    async addToCart(variantId, includeSections = false) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (includeSections && cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map(s => s.id));
        formData.append('sections_url', window.location.pathname);
      }

      const config = {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: formData
      };

      const response = await fetch('/cart/add.js', config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || 'Could not add to cart');
      }

      return data;
    }

    openCartDrawer(response) {
      if (!response) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
      }
    }

    // ----- ERROR -----

    showError(message) {
      if (this.errorText) this.errorText.textContent = message;
      this.errorContainer?.classList.remove('hidden');
      setTimeout(() => this.hideError(), 5000);
    }

    hideError() {
      this.errorContainer?.classList.add('hidden');
    }

    // ----- HELPERS -----

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const raw = format
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
      return this.stripHtml(raw);
    }

    stripHtml(str) {
      return str ? str.replace(/<[^>]*>/g, '') : str;
    }
  }

  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}

/**
 * Shop The Look Inline Rows
 * Per-row option selectors + quick add on the PDP block
 */
if (!customElements.get('stl-inline')) {
  class STLInline extends HTMLElement {
    constructor() {
      super();
      this.rows = this.querySelectorAll('[data-stl-inline-row]');
      this.rows.forEach(row => this.bindRow(row));

      // Collapse any open swatch picker on outside click
      document.addEventListener('click', (e) => {
        this.rows.forEach(row => {
          if (row._collapseSwatches && !row.contains(e.target)) {
            row._collapseSwatches();
          }
        });
      });
    }

    bindRow(row) {
      const variantsScript = row.querySelector('[data-stl-inline-variants]');
      const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      const chips = row.querySelectorAll('[data-stl-chip]');
      const selects = row.querySelectorAll('[data-stl-select]');
      const swatches = row.querySelectorAll('[data-stl-swatch]');
      const swatchesPanel = row.querySelector('[data-stl-swatches]');
      const addBtn = row.querySelector('[data-stl-inline-add]');
      const priceEl = row.querySelector('[data-stl-inline-price]');
      const thumbImg = row.querySelector('[data-stl-inline-thumb] img');
      const hasOnlyDefault = row.dataset.hasOnlyDefault === 'true';
      const colorPosition = parseInt(row.dataset.colorPosition) || 0;
      const hasColor = colorPosition > 0;

      // Selected option values keyed by position (1-indexed)
      const selected = {};

      // Pre-pick a default color so size availability uses a real colorway
      if (hasColor) {
        const firstAvail = variants.find(v => v.available);
        if (firstAvail) selected[colorPosition] = firstAvail.options[colorPosition - 1];
      }

      // What option positions need to be chosen? (everything except color)
      const requiredPositions = new Set();
      chips.forEach(c => requiredPositions.add(parseInt(c.dataset.optionPosition)));
      selects.forEach(s => requiredPositions.add(parseInt(s.dataset.optionPosition)));

      const chipsResolved = () => {
        for (const pos of requiredPositions) {
          if (!selected[pos]) return false;
        }
        return true;
      };

      const resolveVariant = (override = {}) => {
        if (hasOnlyDefault) return variants[0] || null;
        const merged = { ...selected, ...override };
        return variants.find(v => {
          if (!v.available) return false;
          return Object.entries(merged).every(([pos, val]) => v.options[pos - 1] === val);
        }) || null;
      };

      // Update select option availability based on currently-selected color
      const refreshSelectAvailability = () => {
        selects.forEach(sel => {
          const pos = parseInt(sel.dataset.optionPosition);
          Array.from(sel.options).forEach(opt => {
            if (!opt.value) return;
            const test = hasColor ? { [colorPosition]: selected[colorPosition] } : {};
            test[pos] = opt.value;
            const ok = variants.some(v => {
              if (!v.available) return false;
              return Object.entries(test).every(([p, val]) => v.options[p - 1] === val);
            });
            opt.disabled = !ok;
            const baseLabel = opt.dataset.baseLabel || opt.textContent.replace(/\s*–\s*Sold Out$/, '');
            opt.dataset.baseLabel = baseLabel;
            opt.textContent = ok ? baseLabel : `${baseLabel} – Sold Out`;
          });
        });
      };

      // Update chip availability based on currently-selected color
      const refreshChipAvailability = () => {
        refreshSelectAvailability();
        chips.forEach(chip => {
          const pos = parseInt(chip.dataset.optionPosition);
          const value = chip.dataset.value;
          // Test variant matching this chip + current color (ignore other chip selections)
          const test = hasColor ? { [colorPosition]: selected[colorPosition] } : {};
          test[pos] = value;
          const ok = variants.some(v => {
            if (!v.available) return false;
            return Object.entries(test).every(([p, val]) => v.options[p - 1] === val);
          });
          chip.classList.toggle('is-unavailable', !ok);
          if (!ok) chip.setAttribute('aria-disabled', 'true');
          else chip.removeAttribute('aria-disabled');
        });
      };

      const updatePriceAndThumb = () => {
        const v = resolveVariant();
        if (v) {
          if (priceEl) priceEl.textContent = (v.formattedPrice || '').replace(/<[^>]*>/g, '');
          if (thumbImg && v.featuredImage) thumbImg.src = v.featuredImage;
        }
      };

      const updateAddState = () => {
        if (hasOnlyDefault) {
          addBtn.disabled = false;
        } else {
          addBtn.disabled = !chipsResolved();
        }
      };

      // Select changes
      selects.forEach(sel => {
        sel.addEventListener('change', () => {
          const pos = parseInt(sel.dataset.optionPosition);
          selected[pos] = sel.value || undefined;
          if (!sel.value) delete selected[pos];
          updatePriceAndThumb();
          updateAddState();
        });
      });

      // Chip clicks
      chips.forEach(chip => {
        chip.addEventListener('click', () => {
          if (chip.classList.contains('is-unavailable')) return;
          const pos = parseInt(chip.dataset.optionPosition);
          // Toggle within group
          row.querySelectorAll(`[data-stl-chip][data-option-position="${pos}"]`).forEach(c => {
            c.classList.remove('is-selected');
          });
          chip.classList.add('is-selected');
          selected[pos] = chip.dataset.value;
          updatePriceAndThumb();
          updateAddState();
        });
      });

      // Swatch clicks → set color, update chips/price/thumb, then add to cart
      const collapseSwatches = () => {
        if (swatchesPanel) swatchesPanel.hidden = true;
        addBtn.hidden = false;
      };
      row._collapseSwatches = collapseSwatches;

      const expandSwatches = () => {
        if (!swatchesPanel) return;
        // Collapse other rows
        this.rows.forEach(r => {
          if (r !== row && r._collapseSwatches) r._collapseSwatches();
        });
        swatchesPanel.hidden = false;
        addBtn.hidden = true;
        // Mark current color as selected
        swatches.forEach(s => {
          s.classList.toggle('is-selected', s.dataset.value === selected[colorPosition]);
        });
      };

      swatches.forEach(swatch => {
        swatch.addEventListener('click', async (e) => {
          e.stopPropagation();
          if (swatch.classList.contains('is-unavailable')) return;
          selected[colorPosition] = swatch.dataset.value;
          swatches.forEach(s => s.classList.toggle('is-selected', s === swatch));
          refreshChipAvailability();
          updatePriceAndThumb();

          // If chips no longer resolve (selected size unavailable for this color), bail
          if (!hasOnlyDefault && !chipsResolved()) {
            collapseSwatches();
            return;
          }
          await doAdd();
          collapseSwatches();
        });
      });

      addBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (addBtn.disabled) return;
        if (hasColor && swatchesPanel) {
          expandSwatches();
          return;
        }
        await doAdd();
      });

      const doAdd = async () => {
        const variant = resolveVariant();
        if (!variant) return;
        const spinner = addBtn.querySelector('.loading__spinner');
        const text = addBtn.querySelector('.stl-block__row-quick-add-text');
        addBtn.disabled = true;
        spinner?.classList.remove('hidden');

        try {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const formData = new FormData();
          formData.append('id', variant.id);
          formData.append('quantity', 1);
          if (cart && typeof cart.getSectionsToRender === 'function') {
            formData.append('sections', cart.getSectionsToRender().map(s => s.id));
            formData.append('sections_url', window.location.pathname);
          }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            body: formData
          });
          const data = await res.json();
          if (data.status) throw new Error(data.description || 'Could not add to cart');

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shop-the-look-inline', cartData: data });
          }
          if (cart && typeof cart.renderContents === 'function') cart.renderContents(data);

          if (text) text.textContent = 'ADDED';
          setTimeout(() => { if (text) text.textContent = 'QUICK ADD'; }, 1500);
        } catch (err) {
          if (text) text.textContent = 'ERROR';
          setTimeout(() => { if (text) text.textContent = 'QUICK ADD'; }, 2000);
        } finally {
          spinner?.classList.add('hidden');
          updateAddState();
        }
      };

      // Init
      refreshChipAvailability();
      updatePriceAndThumb();
      updateAddState();
    }
  }
  customElements.define('stl-inline', STLInline);
}
